import { addChatMessage } from '../chat/chat.js'; // Correct path: up one level, then into chat

// --- 模块作用域变量 ---
let cardsContainer = null;
let loadingPlaceholder = null;
let errorPlaceholder = null;
let emptyPlaceholder = null;

// Callbacks for actions triggered by user interaction
let onDeleteCallback = async (id) => { console.warn('[Cards UI] onDeleteCallback not implemented for id:', id); };
let onEditCallback = async (id) => { console.warn('[Cards UI] onEditCallback not implemented for id:', id); };
let onReminderChangeCallback = async (id, isChecked) => { console.warn('[Cards UI] onReminderChangeCallback not implemented for id:', id, isChecked); };

// --- DOM & Helper Functions ---

/**
 * Updates the visibility of the empty list message based on card count.
 */
function _updateEmptyState() {
    if (!cardsContainer || !emptyPlaceholder) {
        console.warn('[Cards UI] Cannot update empty state: container or placeholder missing.');
        return;
    }
    const cardCount = cardsContainer.querySelectorAll('.subscription-card').length;
    emptyPlaceholder.style.display = cardCount === 0 ? 'block' : 'none';
    console.log(`[Cards UI] Updated empty state. Card count: ${cardCount}`);
}

/**
 * Formats a date string (ISO) into YYYY-MM-DD.
 * @param {string | null | undefined} dateString
 * @returns {string}
 */
function _formatDate(dateString) {
    if (!dateString) return '-';
    try { return new Date(dateString).toISOString().split('T')[0]; }
    catch (e) { return '无效日期'; }
}

/**
 * Formats amount and currency.
 * @param {number | null | undefined} amount
 * @param {string | null | undefined} currency
 * @returns {string}
 */
function _formatAmount(amount, currency) {
    if (amount === null || amount === undefined) return '-';
    return `${currency || ''}${amount}`;
}

/**
 * Creates the HTML element for a single subscription card.
 * @param {object} subscription - The subscription data.
 * @returns {HTMLElement}
 */
function _createSubscriptionCardElement(subscription) {
    const cardId = subscription.id;
    const isLocal = cardId && cardId.startsWith('temp-');

    const card = document.createElement('div');
    card.classList.add('subscription-card');
    card.setAttribute('data-id', cardId);
    if (isLocal) {
        card.classList.add('local-item');
        card.title = '此记录已本地暂存，将在登录并同步后更新。';
    }

    // --- Card Header ---
    const cardHeader = document.createElement('div');
    cardHeader.classList.add('card-header');
    const cardTitle = document.createElement('h3');
    cardTitle.classList.add('card-title');
    cardTitle.textContent = subscription.name || (isLocal ? '(本地暂存记录)' : '未命名订阅');
    const cardActions = document.createElement('div');
    cardActions.classList.add('card-actions');

    const editSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"></path></svg>`;
    const deleteSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"></path></svg>`;
    const spinnerSvg = `<svg class="spinner-small" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle></svg>`; // Assuming .spinner-small CSS exists

    const editButton = document.createElement('button');
    editButton.classList.add('btn-icon', 'btn-edit');
    editButton.title = '编辑 (功能待实现)';
    editButton.setAttribute('aria-label', '编辑订阅');
    editButton.innerHTML = editSvg;
    editButton.disabled = true; // TODO: Enable edit functionality later
    cardActions.appendChild(editButton);

    const deleteButton = document.createElement('button');
    deleteButton.classList.add('btn-icon', 'btn-delete'); // Single class, action depends on context
    deleteButton.title = isLocal ? '移除此本地记录' : '删除此订阅';
    deleteButton.setAttribute('aria-label', deleteButton.title);
    deleteButton.innerHTML = deleteSvg;
    cardActions.appendChild(deleteButton);

    cardHeader.appendChild(cardTitle);
    cardHeader.appendChild(cardActions);

    // --- Card Content ---
    const cardContent = document.createElement('div');
    cardContent.classList.add('card-content');
    const fields = [
        { label: '金额', value: _formatAmount(subscription.amount, subscription.currency), class: 'amount' },
        { label: '下次扣费日期', value: _formatDate(subscription.nextBillingDate), class: 'date' },
        { label: '付款周期', value: subscription.paymentCycle || '-' },
        // { label: '添加日期', value: _formatDate(subscription.createdAt) } // Optional
    ];
    fields.forEach(field => {
        const fieldDiv = document.createElement('div');
        fieldDiv.classList.add('card-field');
        const labelSpan = document.createElement('span');
        labelSpan.classList.add('card-field-label');
        labelSpan.textContent = field.label;
        const valueSpan = document.createElement('span');
        valueSpan.classList.add('card-field-value');
        if (field.class) valueSpan.classList.add(field.class);
        valueSpan.textContent = field.value;
        fieldDiv.appendChild(labelSpan);
        fieldDiv.appendChild(valueSpan);
        cardContent.appendChild(fieldDiv);
    });

    // --- Card Footer (Reminder) ---
    const cardFooter = document.createElement('div');
    cardFooter.classList.add('card-footer');
    const reminderToggle = document.createElement('div');
    reminderToggle.classList.add('reminder-toggle');
    if (isLocal) {
        reminderToggle.classList.add('disabled'); // Disable toggle for local-only items
    }
    const reminderLabel = document.createElement('label');
    const reminderCheckbox = document.createElement('input');
    reminderCheckbox.type = 'checkbox';
    reminderCheckbox.classList.add('reminder-checkbox');
    reminderCheckbox.checked = subscription.reminder !== false; // Default true if field exists
    reminderCheckbox.disabled = isLocal;
    const reminderText = document.createElement('span');
    reminderText.textContent = ' 提醒';
    reminderLabel.appendChild(reminderCheckbox);
    reminderLabel.appendChild(reminderText);
    reminderToggle.appendChild(reminderLabel);
    cardFooter.appendChild(reminderToggle);

    // Assemble Card
    card.appendChild(cardHeader);
    card.appendChild(cardContent);
    card.appendChild(cardFooter);

    return card;
}

/**
 * Handles clicks within the cards container using event delegation.
 * @param {Event} event - The click event.
 */
async function _handleCardAction(event) {
    const button = event.target.closest('button.btn-icon, input.reminder-checkbox');
    if (!button) return;

    const card = button.closest('.subscription-card');
    if (!card) return;

    const subscriptionId = card.getAttribute('data-id');
    if (!subscriptionId) return;

    // --- Reminder Change Action ---
    if (button.classList.contains('reminder-checkbox')) {
        const isChecked = button.checked;
        console.log(`[Cards UI] Reminder changed for ${subscriptionId} to ${isChecked}`);
        button.disabled = true; // Disable during async operation
        try {
            await onReminderChangeCallback(subscriptionId, isChecked);
            addChatMessage(`订阅 ${card.querySelector('.card-title')?.textContent || subscriptionId} 的提醒已${isChecked ? '开启' : '关闭'}。`, 'system');
        } catch (error) {
            console.error(`[Cards UI] Error calling onReminderChangeCallback for ${subscriptionId}:`, error);
            addChatMessage(`更新提醒失败: ${error.message}`, 'system');
            button.checked = !isChecked; // Revert on error
        } finally {
            button.disabled = false;
        }
        return; // Stop further processing for checkbox change
    }

    // --- Edit Action ---
    if (button.classList.contains('btn-edit')) {
        if (button.disabled) return;
        console.log(`[Cards UI] Edit action triggered for ${subscriptionId}`);
        // TODO: Implement edit confirmation/modal later
        addChatMessage('编辑功能正在开发中。', 'system');
        // await onEditCallback(subscriptionId);
        return;
    }

    // --- Delete Action ---
    if (button.classList.contains('btn-delete')) {
        const isLocalItem = subscriptionId.startsWith('temp-');
        const confirmMsg = isLocalItem
            ? '您确定要移除这条本地暂存记录吗？'
            : '您确定要永久删除这条订阅记录吗？此操作无法撤销。';

        if (confirm(confirmMsg)) {
            button.disabled = true;
            const originalIcon = button.innerHTML;
            button.innerHTML = '<span class="spinner-small"></span>'; // Show loading
            console.log(`[Cards UI] Delete action confirmed for ${subscriptionId}`);
            try {
                await onDeleteCallback(subscriptionId);
                // UI removal is handled by removeSubscriptionCard called externally after successful deletion
                // card.remove(); // Don't remove here, let main logic control UI based on storage/API success
                // _updateEmptyState();
                addChatMessage(`记录 ${card.querySelector('.card-title')?.textContent || subscriptionId} 已删除。`, 'system');
            } catch (error) {
                 console.error(`[Cards UI] Error calling onDeleteCallback for ${subscriptionId}:`, error);
                 addChatMessage(`删除失败: ${error.message}`, 'system');
                 button.disabled = false; // Re-enable on error
                 button.innerHTML = originalIcon;
            }
        }
        return;
    }
}

// --- Public API ---

/**
 * Initializes the cards UI module.
 * @param {object} options
 * @param {HTMLElement} options.container - The DOM element to render cards into.
 * @param {HTMLElement} options.loadingPlaceholder - The loading placeholder element.
 * @param {HTMLElement} options.errorPlaceholder - The error placeholder element.
 * @param {HTMLElement} options.emptyPlaceholder - The empty list placeholder element.
 * @param {function(id: string): Promise<void>} options.onDelete - Callback when delete is confirmed.
 * @param {function(id: string): Promise<void>} options.onEdit - Callback when edit is triggered.
 * @param {function(id: string, isChecked: boolean): Promise<void>} options.onReminderChange - Callback for reminder toggle.
 */
export function initCards({ 
    container, 
    loadingPlaceholder: loadingEl,
    errorPlaceholder: errorEl,
    emptyPlaceholder: emptyEl,
    onDelete, 
    onEdit, 
    onReminderChange 
}) { 
    console.log('[Cards UI] Initializing...');
    
    if (!container) {
        console.error('[Cards UI] Initialization failed: Container element is required.');
        return;
    }
    cardsContainer = container;
    loadingPlaceholder = loadingEl;
    errorPlaceholder = errorEl;
    emptyPlaceholder = emptyEl;

    // Store callbacks
    onDeleteCallback = onDelete || onDeleteCallback;
    onEditCallback = onEdit || onEditCallback;
    onReminderChangeCallback = onReminderChange || onReminderChangeCallback;

    // Use event delegation on the container for card actions
    cardsContainer.addEventListener('click', _handleCardAction);
    console.log('[Cards UI] Event listener added to cards container.');

    console.log('[Cards UI] Initialization complete.');
}

/**
 * Renders a list of subscription cards into the container.
 * Clears existing cards first.
 * @param {Array<object>} subscriptions - An array of subscription objects.
 */
export function renderSubscriptionCards(subscriptions) {
    if (!cardsContainer) {
        console.error('[Cards UI] Cannot render cards: Container not initialized.');
        return;
    }
    console.log(`[Cards UI] Rendering ${subscriptions.length} subscription cards...`);
    // Clear previous cards
    cardsContainer.innerHTML = ''; 
    // Hide placeholders initially
    if (loadingPlaceholder) loadingPlaceholder.classList.add('hidden');
    if (errorPlaceholder) errorPlaceholder.classList.add('hidden');

    if (subscriptions.length === 0) {
        _updateEmptyState();
        return;
    }

    // Append new cards
    subscriptions.forEach(sub => {
        try {
             const cardElement = _createSubscriptionCardElement(sub);
             cardsContainer.appendChild(cardElement);
        } catch (error) {
             console.error(`[Cards UI] Error creating card element for subscription:`, sub, error);
             // Optionally show an error message or skip the card
        }
    });

    _updateEmptyState(); // Update empty state after rendering
}

/**
 * Adds a single subscription card to the container.
 * If a card with the same ID exists, it updates it instead.
 * @param {object} subscription - The subscription data.
 */
export function addSubscriptionCard(subscription) {
    if (!cardsContainer) return;
    console.log(`[Cards UI] Adding/Updating card for ID: ${subscription.id}`);
    const existingCard = cardsContainer.querySelector(`.subscription-card[data-id="${subscription.id}"]`);
    const newCardElement = _createSubscriptionCardElement(subscription);

    if (existingCard) {
        cardsContainer.replaceChild(newCardElement, existingCard);
    } else {
        cardsContainer.appendChild(newCardElement); // Append if new
    }
    _updateEmptyState();
}

/**
 * Removes a subscription card from the container by its ID.
 * @param {string} id - The ID of the subscription card to remove.
 */
export function removeSubscriptionCard(id) {
    if (!cardsContainer) return;
    console.log(`[Cards UI] Removing card with ID: ${id}`);
    const cardToRemove = cardsContainer.querySelector(`.subscription-card[data-id="${id}"]`);
    if (cardToRemove) {
        cardToRemove.remove();
        _updateEmptyState();
    } else {
        console.warn(`[Cards UI] Card with ID ${id} not found for removal.`);
    }
}

/**
 * Updates an existing subscription card in the container.
 * Finds the card by ID and replaces its content.
 * @param {object} subscription - The updated subscription data.
 */
export function updateSubscriptionCard(subscription) {
    if (!cardsContainer) return;
    console.log(`[Cards UI] Updating card for ID: ${subscription.id}`);
    const cardToUpdate = cardsContainer.querySelector(`.subscription-card[data-id="${subscription.id}"]`);
    if (cardToUpdate) {
        const newCardElement = _createSubscriptionCardElement(subscription);
        // Replace the old card with the new one to reflect all changes
        cardsContainer.replaceChild(newCardElement, cardToUpdate);
        _updateEmptyState(); // State might not change, but call for consistency
    } else {
        console.warn(`[Cards UI] Card with ID ${subscription.id} not found for update. Adding instead.`);
        // If update target doesn't exist, add it as a new card
        addSubscriptionCard(subscription); 
    }
}

/**
 * Shows the loading placeholder.
 * @param {string} [message='正在加载订阅列表...'] - Optional message.
 */
export function showLoading(message = '正在加载订阅列表...') {
    if (loadingPlaceholder) {
        loadingPlaceholder.textContent = message;
        loadingPlaceholder.classList.remove('hidden');
    }
    if (errorPlaceholder) errorPlaceholder.classList.add('hidden');
    if (emptyPlaceholder) emptyPlaceholder.classList.add('hidden');
    if (cardsContainer) cardsContainer.innerHTML = ''; // Clear cards when loading
}

/**
 * Shows the error placeholder.
 * @param {string} [message='加载订阅时出错。'] - Optional error message.
 */
export function showError(message = '加载订阅时出错。') {
    if (errorPlaceholder) {
        errorPlaceholder.textContent = message;
        errorPlaceholder.classList.remove('hidden');
    }
    if (loadingPlaceholder) loadingPlaceholder.classList.add('hidden');
    if (emptyPlaceholder) emptyPlaceholder.classList.add('hidden');
    if (cardsContainer) cardsContainer.innerHTML = ''; // Clear cards on error
}
